import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Permission } from '@asi/shared';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth-store';

interface ProtectedRouteProps {
  /** Route is accessible if user has this permission */
  require?: Permission;
  /** Route is accessible if user has ANY of these permissions */
  requireAny?: Permission[];
  children: ReactNode;
}

/**
 * Wraps a route and redirects to /unauthorized if the user lacks the required permission.
 *
 * Usage:
 *   <Route path="users" element={
 *     <ProtectedRoute require="users.view">
 *       <UsersPage />
 *     </ProtectedRoute>
 *   } />
 */
export function ProtectedRoute({ require, requireAny, children }: ProtectedRouteProps) {
  const { can, canAny } = usePermissions();
  const loading = useAuthStore((s) => s.loading);

  // Never redirect while auth state is still loading — prevents spurious
  // /unauthorized flashes when the profile is temporarily null during a
  // token refresh or session re-validation.
  if (loading) return null;

  let allowed = true;

  if (require !== undefined) {
    allowed = can(require);
  }

  if (allowed && requireAny !== undefined) {
    allowed = canAny(requireAny);
  }

  if (!allowed) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
